import React, { useState, useEffect } from 'react';
import { RefreshCw, Mail, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import axios from 'axios';

function PendingOrdersTab({ adminToken }) {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingSlotsLoading, setOpeningSlotsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchPendingOrders();
  }, []);

  const fetchPendingOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/pending-orders');
      setPendingOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Bestellungen' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSlots = async () => {
    if (!window.confirm(`Wirklich alle ${pendingOrders.length} Kunden benachrichtigen und Slots öffnen?`)) {
      return;
    }

    try {
      setOpeningSlotsLoading(true);
      const res = await axios.post('/api/admin/open-slots', {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMessage({
        type: 'success',
        text: `✅ ${res.data.count} Kunden benachrichtigt! Slots sind jetzt verfügbar.`
      });
      await fetchPendingOrders();
    } catch (error) {
      console.error('Error opening slots:', error);
      setMessage({ type: 'error', text: 'Fehler beim Öffnen der Slots' });
    } finally {
      setOpeningSlotsLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId, customerName) => {
    if (!window.confirm(`Bestellung von ${customerName} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      const response = await axios.delete(`/api/admin/pending-orders/${orderId}`);
      
      if (response.data.success || response.data.deleted) {
        setMessage({
          type: 'success',
          text: `✅ Bestellung von ${customerName} gelöscht!`
        });
        await fetchPendingOrders();
      }
    } catch (error) {
      console.error('Delete error details:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.error || 'Fehler beim Löschen der Bestellung';
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin">
          <RefreshCw size={32} className="text-purple-400" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Message */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg border-l-4 flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-900/30 border-green-500 text-green-200'
              : 'bg-red-900/30 border-red-500 text-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          {message.text}
        </div>
      )}

      {/* Summary Card */}
      <div
        style={{
          background: 'linear-gradient(to bottom right, #6b21a8, #1e293b)',
          border: '2px solid rgba(168, 85, 247, 0.5)',
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem'
        }}>
          <div>
            <div style={{ color: '#a0aec0', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Ausstehende Bestellungen</div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#fff' }}>{pendingOrders.length}</div>
          </div>
          <div>
            <div style={{ color: '#a0aec0', fontSize: '0.875rem', marginBottom: '0.25rem' }}>Gesamtbetrag</div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', color: '#fff' }}>
              €{pendingOrders.reduce((sum, o) => sum + (o.amount * 1.19), 0).toFixed(2)}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
            <button
              onClick={handleOpenSlots}
              disabled={pendingOrders.length === 0 || openingSlotsLoading}
              style={{
                background: pendingOrders.length === 0 || openingSlotsLoading 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'linear-gradient(to right, #059669, #10b981)',
                border: 'none',
                color: '#fff',
                fontWeight: 'bold',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                cursor: pendingOrders.length === 0 || openingSlotsLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                opacity: pendingOrders.length === 0 || openingSlotsLoading ? 0.5 : 1
              }}
            >
              {openingSlotsLoading ? (
                <>
                  <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Wird versendet...
                </>
              ) : (
                <>
                  <Mail size={16} />
                  Slots öffnen & E-Mails
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      {pendingOrders.length > 0 ? (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid rgba(168, 85, 247, 0.3)',
          borderRadius: '0.5rem',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.875rem', color: '#e2e8f0' }}>
              <thead style={{ background: 'rgba(109, 40, 217, 0.3)', borderBottom: '1px solid rgba(168, 85, 247, 0.3)' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Paket</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Betrag</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Gutschein</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Zahlungsart</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Adresse</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 'bold' }}>Datum</th>
                  <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold' }}>Aktion</th>
                </tr>
              </thead>
              <tbody style={{ borderTop: '1px solid #475569' }}>
                {pendingOrders.map((order, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #475569', transition: 'background-color 0.3s' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(51, 65, 85, 0.2)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '1rem', fontWeight: 'bold', color: '#d8b4fe' }}>
                      {order.firstName} {order.lastName}
                    </td>
                    <td style={{ padding: '1rem', color: '#cbd5e1' }}>{order.email}</td>
                    <td style={{ padding: '1rem', color: '#cbd5e1', textTransform: 'capitalize' }}>{order.packageId}</td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: '#cbd5e1' }}>
                      €{(order.amount * 1.19).toFixed(2)}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <code style={{
                        background: 'rgba(51, 65, 85, 0.5)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        color: '#4ade80',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        {order.voucherCode}
                      </code>
                    </td>
                    <td style={{ padding: '1rem', color: '#cbd5e1', textTransform: 'capitalize' }}>
                      {order.paymentMethod === 'stripe' ? '💳' : '🔵'} {order.paymentMethod}
                    </td>
                    <td style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.75rem' }}>
                      {order.address}, {order.postalCode}
                    </td>
                    <td style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.75rem' }}>
                      {new Date(order.createdAt).toLocaleDateString('de-DE')}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button
                        onClick={() => handleDeleteOrder(order.id, `${order.firstName} ${order.lastName}`)}
                        style={{
                          background: 'rgba(153, 27, 27, 0.3)',
                          border: 'none',
                          color: '#fca5a5',
                          padding: '0.5rem',
                          borderRadius: '0.25rem',
                          cursor: 'pointer',
                          transition: 'background-color 0.3s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(153, 27, 27, 0.6)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(153, 27, 27, 0.3)'}
                        title="Diese Bestellung löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '2px dashed rgba(168, 85, 247, 0.3)',
          borderRadius: '0.5rem',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <CheckCircle size={48} style={{ margin: '0 auto 1rem', color: '#4ade80', opacity: 0.5 }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#fff' }}>
            Keine ausstehenden Bestellungen!
          </h3>
          <p style={{ color: '#94a3b8' }}>Alle Gutscheine wurden bereits eingelöst.</p>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PendingOrdersTab;
